import { useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp } from "lucide-react";

const GRADE = { "A+": 95, A: 85, "A-": 80, "B+": 75, B: 65, "B-": 60, "C+": 55, C: 45, "C-": 40, D: 35, E: 25, F: 15, O: 100 };
const COLORS = ["#7c3aed", "#ec4899", "#0ea5e9", "#f59e0b", "#10b981", "#ef4444", "#6366f1", "#14b8a6"];
const TERM_ORDER = (t) => { const m = /(\d+)/.exec(t); if (/half/i.test(t)) return 1.5; if (/annual|final/i.test(t)) return 9; return m ? Number(m[1]) : 5; };

export function scoreOf(s) {
  const m = String(s.marks || "").trim();
  const frac = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/.exec(m);
  if (frac && Number(frac[2]) > 0) return Math.round((Number(frac[1]) / Number(frac[2])) * 100);
  if (/^\d+(\.\d+)?%?$/.test(m) && Number(m.replace("%", "")) <= 100) return Math.round(Number(m.replace("%", "")));
  const g = GRADE[String(s.grade || "").trim().toUpperCase()];
  return g ?? null;
}

export function ProgressChart({ reports }) {
  const { data, subjects } = useMemo(() => {
    const sorted = [...reports].sort((a, b) => a.academic_year.localeCompare(b.academic_year) || TERM_ORDER(a.term) - TERM_ORDER(b.term));
    const subs = new Set();
    const rows = sorted.map((r) => {
      const point = { label: `${r.term} ${r.academic_year}` };
      const vals = [];
      r.subjects.forEach((s) => { const v = scoreOf(s); if (v != null) { point[s.subject] = v; subs.add(s.subject); vals.push(v); } });
      point.Overall = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
      return point;
    });
    return { data: rows, subjects: [...subs] };
  }, [reports]);
  const [show, setShow] = useState("Overall");
  if (data.length < 1 || !data.some((d) => d.Overall != null)) return null;
  const lines = show === "all" ? subjects : [show];
  return (
    <div data-testid="progress-chart" className="bg-white rounded-2xl shadow-sm p-4 mb-5">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <TrendingUp size={18} className="text-violet-600" />
        <h3 className="font-display font-bold text-slate-900">Progress across terms</h3>
        <div className="ml-auto flex flex-wrap gap-1">
          {["Overall", ...subjects, ...(subjects.length > 1 ? ["all"] : [])].map((k) => (
            <button key={k} data-testid={`chart-show-${k}`} onClick={() => setShow(k)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${show === k ? "bg-violet-600 text-white" : "bg-violet-50 text-violet-700 hover:bg-violet-100"}`}>{k === "all" ? "All subjects" : k}</button>
          ))}
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede9fe" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => `${v}%`} />
            {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {lines.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={k === "Overall" ? "#7c3aed" : COLORS[(subjects.indexOf(k) + 1) % COLORS.length] || COLORS[i]} strokeWidth={3} dot={{ r: 4 }} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">Scores use marks (e.g. 45/50 or 90%) or, if no marks, the grade (A+ ≈ 95, A ≈ 85, B ≈ 65 …).{data.length === 1 ? " The line appears once a second term is posted." : ""}</p>
    </div>
  );
}
